import { useEffect, useRef, useState } from 'react';
import { SiteDocumentList } from '../components/SiteDocuments';
import { PhotoStrip } from '../components/ResidencePhotos';
import { useReloadOnChange } from '../lib/changes';
import { ApiError, api, slotLabel, type Flight, type Lodging, type MyStint, type PhysicianSummary, type TripPlanInput } from '../lib/api';
import { addDays, dayLabel, isoToday } from '../lib/dates';

/**
 * The physician's "My trip" page (D33): one card per current or upcoming
 * stint with their own lines from the logistics page — who they take over
 * from and hand off to, where they sleep and with whom, the site guide —
 * and the "before you go" questions that replace OSMD's pre-tour survey.
 * The residence address and door codes appear only from three days before
 * arrival; until then the card says when they will. James asked for this
 * as its own tab rather than a card above the shift list, and for a friendly
 * name — "My tour" read as military to him, "My assignment" as formal (Sep 19).
 */
export function Tour({ physician }: { physician: PhysicianSummary }) {
  const today = isoToday();
  const [stints, setStints] = useState<MyStint[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = () => api.myStints(physician.abbreviation, addDays(today, -7), addDays(today, 366)).then((rows) => setStints(rows.filter((s) => s.end >= today))).catch((e: Error) => setError(e.message));
  useEffect(() => {
    load();
  }, [physician.abbreviation]);
  useReloadOnChange(load);

  if (error) return <p className="error">Couldn't load your tour details ({error}).</p>;
  if (!stints) return <p className="muted">Loading…</p>;
  return (
    <section className="tourpage">
      <h1>My trip</h1>
      <p className="summary">Handoffs, where you're staying, the site guide, and the questions the logistics coordinator needs answered before you travel.</p>
      {stints.length === 0 ? (
        <div className="card">
          <p className="muted">Nothing scheduled ahead yet. When a week of yours is published, it appears here with who you take over from, your room, and the site guide.</p>
        </div>
      ) : (
        <div className="yourweek">
          {stints.map((s, i) => <StintCard key={`${s.siteId}|${s.start}`} stint={s} physician={physician} today={today} open={i === 0} onSaved={load} />)}
        </div>
      )}
    </section>
  );
}

const short = (d: string) => dayLabel(d).replace(/^\w+, /, '');

function StintCard({ stint: s, physician, today, open, onSaved }: { stint: MyStint; physician: PhysicianSummary; today: string; open: boolean; onSaved: () => void }) {
  const current = s.start <= today && today <= s.end;
  const weeks = Math.round((Date.parse(s.end) - Date.parse(s.start)) / (7 * 86400000));
  const showsFrom = addDays(s.start, -3);
  return (
    <details className={`card stintcard${current ? ' current' : ''}`} open={open}>
      <summary>
        <span className={`site site-${s.siteId}`}>{s.siteName}</span>
        <span className="stintwhen">{current ? 'This trip' : 'Upcoming'}: Tue {short(s.start)} – Tue {short(s.end)} <span className="muted">· {weeks} week{weeks === 1 ? '' : 's'} · {s.slots.map(slotLabel).join(', ')}</span></span>
        {s.status !== 'published' && <span className="pill tiny warn">awaiting publication</span>}
      </summary>
      <div className="stintbody">
        <p>
          {s.handoff.takesOverFrom ? <>You take over from <strong>{s.handoff.takesOverFrom}</strong> at {s.handoff.at} on Tue {short(s.start)}.</> : <>You start Tue {short(s.start)}.</>}{' '}
          {s.handoff.handsOffTo ? <>You hand off to <strong>{s.handoff.handsOffTo}</strong> at {s.handoff.at} on Tue {short(s.end)}.</> : <>You finish the morning of Tue {short(s.end)}.</>}
        </p>
        {s.stay ? (
          <div className="stay">
            <p>
              <strong>Where you're staying:</strong> {s.stay.residence} · {s.stay.room}
              {s.stay.status === 'proposed' && <span className="pill tiny warn">to be confirmed</span>}
              {s.stay.roommates.length > 0 && <span className="muted"> · with {s.stay.roommates.map((r) => `${r.name} (${r.room})`).join(', ')}</span>}
            </p>
            {s.stay.address ? (
              <p className="small">
                {s.stay.address}
                {s.stay.accessNotes && <><br /><span className="muted">Access:</span> {s.stay.accessNotes}</>}
              </p>
            ) : (
              <p className="muted small">The address and door codes will appear here from {dayLabel(showsFrom)}.</p>
            )}
            {s.stay.entryMethod && <p className="small"><span className="muted">Getting in:</span> {s.stay.entryMethod}</p>}
            {(s.stay.contactName || s.stay.contactPhone) && (
              <p className="small">
                <span className="muted">About the house:</span> {s.stay.contactName}
                {s.stay.contactPhone && <> · <a href={`tel:${s.stay.contactPhone.replace(/[^\d+]/g, '')}`}>{s.stay.contactPhone}</a></>}
              </p>
            )}
            {s.stay.wifi && <p className="small"><span className="muted">WiFi:</span> {s.stay.wifi}</p>}
            {s.stay.parking && <p className="small"><span className="muted">Parking:</span> {s.stay.parking}</p>}
            {s.stay.notes && <p className="small muted">{s.stay.notes}</p>}
            {/* The pictures arrive with the address and on the same rule (D73): at eleven at
                night "exterior stairs beside the Recreation Center" is a sentence, and a
                photograph of the stairs is the thing that actually helps. */}
            {s.stay.residenceId && s.stay.photos.length > 0 && <PhotoStrip residenceId={s.stay.residenceId} photos={s.stay.photos} />}
          </div>
        ) : (
          <p className="muted small">
            {/* Where OSMD keeps no house, say so and say what happens instead (D68) — Jasper is a
                hotel at the hospital's expense. "No room assigned yet" would be a lie there. */}
            {s.lodgingNote
              ? s.lodgingNote
              : s.plan?.lodging === 'home'
                ? "You told us you're driving in from home this trip."
                : s.plan?.lodging === 'own'
                  ? "You told us you're making your own arrangements this trip."
                  : 'No room assigned yet — the logistics coordinator will confirm one before you arrive.'}
          </p>
        )}
        <GettingThere stint={s} />
        {s.guide && (
          <details className="guidebox small">
            <summary>Site guide — {s.siteName}</summary>
            <pre className="guide">{s.guide}</pre>
          </details>
        )}
        {/* The hospital's own documents (D98) — what the guide used to tell them to email for. */}
        <SiteDocumentList siteId={s.siteId} documents={s.documents ?? []} />
        <PlanForm stint={s} physician={physician} onSaved={onSaved} />
      </div>
    </details>
  );
}

/**
 * Flights with their live status (D36, narrowed by D62).
 *
 * The Uber and Lyft buttons used to sit here too. They are the coordinator's to use now and
 * live on the logistics week instead — one person arranges the ride, so one page offers it.
 * A physician who has asked for an airport transfer is told the office is arranging it
 * rather than being shown a control that is no longer theirs.
 */
function GettingThere({ stint: s }: { stint: MyStint }) {
  const flights = s.flights ?? [];
  const plan = s.plan;
  const wantsRide = !!(plan?.arrivalAirport || plan?.departureAirport);
  if (flights.length === 0 && !wantsRide) return null;
  return (
    <div className="getting-there">
      {flights.length > 0 && (
        <ul className="flightlist">
          {flights.map((f, i) => (
            <li key={i}>
              <span className="leg">{f.leg === 'arrival' ? 'In' : 'Out'}</span>
              <strong>{f.airline} {f.number}</strong> <span className="muted">{short(f.date)}{f.origin && f.destination ? ` · ${f.origin} → ${f.destination}` : ''}</span>
              <span className={`pill tiny fl-${f.status}`}>{f.summary}</span>
            </li>
          ))}
        </ul>
      )}
      {wantsRide && <p className="muted small">Your airport ride is arranged by the logistics coordinator — you don't need to book it.</p>}
    </div>
  );
}

/** The "before you go" questions: airport transfers, the residence, groceries, flights, anything else. */
function PlanForm({ stint: s, physician, onSaved }: { stint: MyStint; physician: PhysicianSummary; onSaved: () => void }) {
  const p = s.plan;
  const [needsArrival, setNeedsArrival] = useState<boolean | null>(p?.needsArrivalTransfer ?? null);
  // The airport OSMD's physicians actually fly into for this hospital (D68) — offered rather
  // than left blank, because flight tracking can only watch an airport somebody got right.
  const [arrivalAirport, setArrivalAirport] = useState(p?.arrivalAirport ?? s.arrivalAirport ?? '');
  const [arrivalAt, setArrivalAt] = useState(p?.arrivalAt ?? '');
  const [arrivalDropoff, setArrivalDropoff] = useState(p?.arrivalDropoff ?? '');
  const [needsDeparture, setNeedsDeparture] = useState<boolean | null>(p?.needsDepartureTransfer ?? null);
  const [departureAirport, setDepartureAirport] = useState(p?.departureAirport ?? '');
  const [departureAt, setDepartureAt] = useState(p?.departureAt ?? '');
  const [departurePickup, setDeparturePickup] = useState(p?.departurePickup ?? '');
  const [lodging, setLodging] = useState<Lodging | null>(p?.lodging ?? null);
  const [flying, setFlying] = useState<boolean | null>(p?.flying ?? null);
  const [roomPreference, setRoomPreference] = useState(p?.roomPreference ?? '');
  const [groceries, setGroceries] = useState((p?.groceries ?? []).join('\n'));
  const [doorDash, setDoorDash] = useState<boolean>(p?.doorDashPass ?? false);
  // One row per leg, filled from what was saved; the date defaults to the stint's edges.
  const [flights, setFlights] = useState<Flight[]>(() => (['arrival', 'departure'] as const).map((leg) => p?.flights.find((f) => f.leg === leg) ?? { leg, airline: '', number: '', date: leg === 'arrival' ? s.start : s.end }));
  const [notes, setNotes] = useState(p?.notes ?? '');
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(p?.submittedAt ? new Date(p.submittedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : null);

  // The form is seeded from `s.plan` ONCE, at mount — that is what `useState(initial)` means.
  // Callie writes to the same plan and the page reloads behind her (see useReloadOnChange), so
  // without this the boxes still show the old answers while the plan holds her new ones. And
  // the save is a REPLACE, not a merge: pressing it then sent `needsArrivalTransfer: null` and
  // `flights: []` over the ride and flight she had just booked. A physician adding "milk" to
  // the grocery list erased their own airport transfer, and nothing said so (D84).
  //
  // Re-seed when the stored plan changes and the form is untouched. When it IS touched, say so
  // rather than throwing away what they are typing — either way they never save over her.
  const [changedElsewhere, setChangedElsewhere] = useState(false);
  const seed = (plan: MyStint['plan']) => {
    setNeedsArrival(plan?.needsArrivalTransfer ?? null);
    setArrivalAirport(plan?.arrivalAirport ?? s.arrivalAirport ?? '');
    setArrivalAt(plan?.arrivalAt ?? '');
    setArrivalDropoff(plan?.arrivalDropoff ?? '');
    setNeedsDeparture(plan?.needsDepartureTransfer ?? null);
    setDepartureAirport(plan?.departureAirport ?? '');
    setDepartureAt(plan?.departureAt ?? '');
    setDeparturePickup(plan?.departurePickup ?? '');
    setLodging(plan?.lodging ?? null);
    setFlying(plan?.flying ?? null);
    setRoomPreference(plan?.roomPreference ?? '');
    setGroceries((plan?.groceries ?? []).join('\n'));
    setDoorDash(plan?.doorDashPass ?? false);
    setFlights((['arrival', 'departure'] as const).map((leg) => plan?.flights.find((f) => f.leg === leg) ?? { leg, airline: '', number: '', date: leg === 'arrival' ? s.start : s.end }));
    setNotes(plan?.notes ?? '');
    setSaved(plan?.submittedAt ? new Date(plan.submittedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : null);
  };
  // By value: the plan carries no version, and any field of it changing matters.
  const planJson = JSON.stringify(p ?? null);
  const seenJson = useRef(planJson);
  useEffect(() => {
    if (planJson === seenJson.current) return;
    seenJson.current = planJson;
    if (dirty) setChangedElsewhere(true);
    else seed(p);
  }, [planJson]);

  const touch = <T,>(set: (v: T) => void) => (v: T) => { set(v); setDirty(true); };
  const setFlight = (i: number, k: keyof Flight, v: string) => touch(setFlights)(flights.map((f, j) => (j === i ? { ...f, [k]: v } : f)));
  const save = async () => {
    // `booked`, not `flying` — the state above is now a boolean and shadowing it here sent the
    // flight ARRAY to the server in the boolean's place.
    const booked = flights.filter((f) => f.airline.trim() || f.number.trim());
    if (booked.some((f) => !f.date)) {
      setProblem('Add the date for each flight, or clear the airline and flight number.');
      return;
    }
    // Required, and refused here rather than quietly saved as a blank (D100). OSMD cannot
    // predict how full its houses will be while this is the one answer most people skip, and
    // a bed counted for somebody who drives home every night is a bed somebody else needed.
    if (lodging === null) {
      setProblem('Please answer where you will be staying — it is how the office works out how many beds are needed.');
      return;
    }
    // Flying is required, and saying yes means both legs (D102). The coordinator meets one
    // flight and books a car for the other; half an answer leaves them guessing on the day.
    if (flying === null) {
      setProblem('Please answer whether you are flying — the coordinator needs to know whether to meet a flight.');
      return;
    }
    if (flying && flights.some((f) => !f.airline.trim() || !f.number.trim() || !f.date)) {
      setProblem('Add the airline, flight number and date for both flights — the one in and the one out.');
      return;
    }
    setBusy(true);
    setProblem(null);
    try {
      const body: TripPlanInput = {
        // A driver has no airport transfer, so nothing about one is sent (D104).
        needsArrivalTransfer: flying ? needsArrival : null,
        arrivalAirport: flying && needsArrival ? arrivalAirport : null,
        arrivalAt: flying && needsArrival ? arrivalAt : null,
        arrivalDropoff: flying && needsArrival ? arrivalDropoff : null,
        needsDepartureTransfer: flying ? needsDeparture : null,
        departureAirport: flying && needsDeparture ? departureAirport : null,
        departureAt: flying && needsDeparture ? departureAt : null,
        departurePickup: flying && needsDeparture ? departurePickup : null,
        lodging,
        flying,
        roomPreference: lodging === 'residence' ? roomPreference : null,
        groceries: groceries.split('\n').map((g) => g.trim()).filter(Boolean),
        doorDashPass: doorDash,
        flights: flying ? flights.map((f) => ({ ...f, airline: f.airline.trim(), number: f.number.trim().toUpperCase() })) : [],
        notes: notes || null,
      };
      const r = await api.savePlan(physician.abbreviation, s.siteId, s.start, body);
      setSaved(r.submittedAt ? new Date(r.submittedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'now');
      setDirty(false);
      onSaved();
    } catch (e) {
      setProblem(e instanceof ApiError ? e.message : (e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  /** One question with three answers rather than two yes/no questions (D100). Asking
   *  "Commuting from home?" and "Staying at the residence?" separately lets somebody answer
   *  yes to both, which is not a thing anyone can do, and the bed count is then built on
   *  answers that disagree with each other. */
  const lodgingChoice = (
    <div className="yesno wide">
      <span>
        Where will you be staying during this trip? <span className="pill tiny warn">required</span>
      </span>
      <span className="choice">
        {([['home', 'Driving from home'], ['residence', 'OSMD residence'], ['own', 'My own arrangements']] as const).map(([k, label]) => (
          <button key={k} type="button" role="radio" aria-checked={lodging === k} className={lodging === k ? 'on' : ''} onClick={() => touch(setLodging)(k)}>
            {label}
          </button>
        ))}
      </span>
    </div>
  );

  // What still has no answer. Both of these opened by themselves for a physician who had
  // already submitted everything else before the question existed (D100, D102).
  const missing = [lodging == null && 'lodging', flying == null && 'flying'].filter(Boolean);

  const yesNo = (value: boolean | null, set: (v: boolean) => void, label: string) => (
    <div className="yesno">
      <span>{label}</span>
      <span className="choice">
        <button type="button" role="radio" aria-checked={value === true} className={value === true ? 'on' : ''} onClick={() => set(true)}>Yes</button>
        <button type="button" role="radio" aria-checked={value === false} className={value === false ? 'on' : ''} onClick={() => set(false)}>No</button>
      </span>
    </div>
  );

  return (
    // Opens by itself for anyone who has not answered yet — including a physician who
    // submitted everything else before the lodging question existed (D100). Their plan is
    // otherwise complete, so nothing else would ever draw them back to the one missing answer.
    <details className="planform" open={!p?.submittedAt || p?.lodging == null || p?.flying == null}>
      <summary>
        Before you go — travel &amp; lodging details{' '}
        {missing.length ? (
          <span className="pill tiny warn">{saved ? (missing.length === 1 ? 'one answer missing' : `${missing.length} answers missing`) : 'please answer'}</span>
        ) : (
          <span className="ok small">answered {saved}</span>
        )}
      </summary>
      <div className="planbody">
        <p className="muted small">This replaces the pre-tour survey. Your answers go straight to the logistics coordinator; change them any time.</p>

        {/* First, and required (D102). It leads the travel questions because it decides
            whether the rest of them mean anything — asking "do you need a ride from the
            airport" of somebody who just said they are driving up reads as not listening.
            And "make the flights compulsory" on its own breaks for exactly those physicians:
            forced to fill a box they have no answer for, somebody types AA 0000, and the
            tracker watches for a flight nobody booked. An explicit no beats a filled-in lie. */}
        {yesNo(flying, touch(setFlying), 'Are you flying to this trip?')}
        {flying && (
          <div className="flights">
            <span className="small muted">Your flights <span>(both needed — it is how the coordinator meets you and watches for delays)</span></span>
            {flights.map((f, i) => (
              <div key={i} className="flightrow">
                <span className="leg">{f.leg === 'arrival' ? 'In' : 'Out'}</span>
                <input value={f.airline} maxLength={40} placeholder="airline, e.g. AA" onChange={(e) => setFlight(i, 'airline', e.target.value)} />
                <input value={f.number} maxLength={10} placeholder="flight no." onChange={(e) => setFlight(i, 'number', e.target.value)} />
                <input type="date" value={f.date} onChange={(e) => setFlight(i, 'date', e.target.value)} />
              </div>
            ))}
          </div>
        )}

        {/* Only for somebody who flies (D104). Asking a physician who has just said they are
            driving up whether they need a ride FROM THE AIRPORT is the platform not listening,
            and an answer to it would be noise on the coordinator's page. */}
        {flying && (
          <>
          {yesNo(needsArrival, touch(setNeedsArrival), `Do you need a ride from the airport when you arrive (Tue ${short(s.start)})?`)}
          {needsArrival && (
            <div className="subfields">
              <label className="field">
                <span>Airport {s.arrivalAirport && <span className="muted">(most physicians fly into {s.arrivalAirport})</span>}</span>
                <input value={arrivalAirport} maxLength={10} placeholder={s.arrivalAirport ? `e.g. ${s.arrivalAirport}` : 'e.g. GSO'} onChange={(e) => touch(setArrivalAirport)(e.target.value.toUpperCase())} />
              </label>
              <label className="field"><span>Landing</span><input type="datetime-local" value={arrivalAt} onChange={(e) => touch(setArrivalAt)(e.target.value)} /></label>
              <label className="field"><span>Drop off at</span>
                <select value={arrivalDropoff} onChange={(e) => touch(setArrivalDropoff)(e.target.value)}>
                  <option value="">— choose —</option>
                  <option value="hospital">the hospital</option>
                  <option value="residence">the residence</option>
                  {arrivalDropoff && !['hospital', 'residence'].includes(arrivalDropoff) && <option value={arrivalDropoff}>{arrivalDropoff}</option>}
                </select>
              </label>
            </div>
          )}

          {yesNo(needsDeparture, touch(setNeedsDeparture), `Do you need a ride to the airport when you leave (Tue ${short(s.end)})?`)}
          {needsDeparture && (
            <div className="subfields">
              <label className="field"><span>Airport</span><input value={departureAirport} maxLength={10} placeholder="e.g. GSO" onChange={(e) => touch(setDepartureAirport)(e.target.value.toUpperCase())} /></label>
              <label className="field"><span>Flight leaves</span><input type="datetime-local" value={departureAt} onChange={(e) => touch(setDepartureAt)(e.target.value)} /></label>
              <label className="field"><span>Pick up from</span>
                <select value={departurePickup} onChange={(e) => touch(setDeparturePickup)(e.target.value)}>
                  <option value="">— choose —</option>
                  <option value="hospital">the hospital</option>
                  <option value="residence">the residence</option>
                  {departurePickup && !['hospital', 'residence'].includes(departurePickup) && <option value={departurePickup}>{departurePickup}</option>}
                </select>
              </label>
            </div>
          )}
          </>
        )}

        {lodgingChoice}
        {lodging === 'residence' && (
          <div className="subfields">
            <label className="field"><span>Room preference <span className="muted">(optional)</span></span><input value={roomPreference} maxLength={200} placeholder="e.g. same room as last time; private room" onChange={(e) => touch(setRoomPreference)(e.target.value)} /></label>
          </div>
        )}

        {/* One or the other (D61): the pass comes first because it is the quicker answer, and
            the choice is said before it is offered rather than explained after it is made. */}
        <p className="notice small">Food for the week: choose <strong>one</strong> — a DoorDash pass, or groceries in before you arrive.</p>
        <label className="field check">
          <input type="checkbox" checked={doorDash} onChange={(e) => { const on = e.target.checked; touch(setDoorDash)(on); if (on) setGroceries(''); }} /> I'd rather have a DoorDash pass
        </label>
        <label className="field">
          <span>Groceries to have in before you arrive <span className="muted">{doorDash ? '(not needed — you asked for a DoorDash pass)' : '(one per line; leave blank for none)'}</span></span>
          <textarea
            rows={3}
            value={groceries}
            disabled={doorDash}
            onChange={(e) => touch(setGroceries)(e.target.value)}
            placeholder={doorDash ? '' : 'milk\ncoffee\nfruit'}
          />
        </label>

        <label className="field">
          <span>Anything else the coordinator should know</span>
          <textarea rows={2} value={notes} maxLength={2000} onChange={(e) => touch(setNotes)(e.target.value)} />
        </label>
        {problem && <p className="error">{problem}</p>}

        {changedElsewhere && (

          <p className="warn small">

            Callie changed these answers while you were editing. Saving now would write over her —{' '}

            <button type="button" className="linklike" onClick={() => { seed(p); setDirty(false); setChangedElsewhere(false); }}>show what she saved</button>{' '}

            (this discards what you have typed here).

          </p>

        )}
        <div className="formactions">
          <button type="button" className="primary" disabled={busy || !dirty} onClick={save}>{busy ? 'Saving…' : p?.submittedAt ? 'Save changes' : 'Send my answers'}</button>
          {dirty && <span className="muted small">Not saved yet</span>}
        </div>
      </div>
    </details>
  );
}
